// Simulations launch hub container (simulations.md; founder-ratified
// 2026-07-26). The queue reads `GET /v1/batches?agent_id=` (newest-first
// keyset): pending/running batches render as live active cards, terminal
// batches as the history table. Results/analysis stay in Transcripts — every
// row links to `/transcripts?batch=`; there is no per-batch route. `batch`
// remains the domain noun (docs/09 §4); "Simulations" labels the surface.
// `?launched=<batchId>` highlights a just-launched batch. Container (queries +
// mutations) / View (props-pure) split.
//
// Templates fold into this screen as its second tab (founder ratification
// 2026-07-31): `?tab=templates` renders the templates table that used to live
// at `/templates`, which is now a redirect here. A Template is a saved launch
// spec, so it belongs beside the launch queue; scheduling is active through
// server-provided state. Authoring a template is a
// Route, not a drawer on this page (UI-LAW §4a): Add and Edit navigate to
// /simulations/templates/new and /simulations/templates/$templateId.
import { useCallback, useMemo } from "react";

import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { createIdempotencyKey } from "../lib/idempotency";
import { queryKeys } from "../lib/queryKeys";
import { isShowcaseMode } from "../lib/showcaseMode";
import {
  parseSimulationsSearch,
  type SimulationsSearch,
  type SimulationsTab,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { useListQuery } from "../lib/useListQuery";
import {
  useResolvedWorkingTeamId,
  useWorkingScope,
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { Button, Page, PageHeader, TabPanel, Tabs, useToast } from "../ui";
import { SimulationQueue } from "./simulations/SimulationQueue";
import { ShowcaseReadOnlyNotice } from "./ShowcaseReadOnlyNotice";
import { TemplatesList } from "./templates/TemplatesList";

type BatchResponse = components["schemas"]["BatchResponse"];

type SimulationsSearchState = SimulationsSearch & WorkingScopeSearch;

const ACTIVE_STATUSES = new Set<BatchResponse["status"]>([
  "pending",
  "running",
]);

const simulationTabs: Array<{ id: SimulationsTab; label: string }> = [
  // The default tab is labeled "Runs" (docs/09 §4: `?tab` absent = Runs),
  // following the sealed "launch N runs" convention.
  { id: "runs", label: "Runs" },
  { id: "templates", label: "Templates" },
];

export function isActiveBatch(batch: BatchResponse): boolean {
  return ACTIVE_STATUSES.has(batch.status);
}

export function Simulations() {
  const { agentId } = useWorkingScope();
  const workingTeamId = useResolvedWorkingTeamId();
  const navigate = useTypedNavigate<SimulationsSearchState>();
  const search = useTypedSearch(withWorkingScopeSearch(parseSimulationsSearch));
  const queryClient = useQueryClient();
  const toast = useToast();
  // The curated static showcase ships the queue only: its read-only fixture
  // client serves no /v1/templates, so a Templates tab there would be a dead
  // facet (UI truthfulness).
  const activeTab: SimulationsTab = isShowcaseMode
    ? "runs"
    : (search.tab ?? "runs");

  // Functional-merge navigation ONLY: patch this route's own params over the
  // previous search so the root-owned `?agent` scope always survives.
  const patchSearch = useCallback(
    (patch: Partial<SimulationsSearch>, replace: boolean) => {
      void navigate({
        replace,
        search: mergeSearch<SimulationsSearchState>(patch),
      });
    },
    [navigate],
  );

  const onTabChange = useCallback(
    (tab: SimulationsTab) => {
      patchSearch({ tab }, false);
    },
    [patchSearch],
  );
  // Template authoring is a Route (UI-LAW §4a): only the working scope rides
  // along, so a stale queue param never follows the author onto the form.
  const openTemplateCreate = useCallback(() => {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) => ({ agent, team }),
      to: "/simulations/templates/new",
    });
  }, [navigate]);
  const openTemplateEdit = useCallback(
    (templateId: string) => {
      void navigate({
        params: { templateId },
        search: ({ agent, team }: WorkingScopeSearch) => ({ agent, team }),
        to: "/simulations/templates/$templateId",
      });
    },
    [navigate],
  );

  // All agents = the working team's data (docs/09 §4); held until resolved.
  // Agent wins: a scoped agent sends `agent_id` only, never both filters.
  const listFilters =
    agentId != null ? { agent_id: agentId } : { team_id: workingTeamId };
  const batches = useListQuery("/v1/batches", {
    enabled: agentId != null || workingTeamId !== undefined,
    params: listFilters,
    queryKey: queryKeys.batches.list(listFilters),
  });
  const templates = useListQuery("/v1/templates", {
    enabled:
      !isShowcaseMode && (agentId != null || workingTeamId !== undefined),
    params: listFilters,
    queryKey: queryKeys.templates.list(listFilters),
  });
  const loadedTemplateIds = useMemo(
    () => new Set(templates.items.map((template) => template.id)),
    [templates.items],
  );
  // Batches are newest-first while templates are oldest-first and omit deleted
  // rows. Resolve each scheduled template absent from the loaded page so its
  // provenance remains named whenever the detail read is available.
  const missingScheduledTemplateIds = useMemo(
    () =>
      [
        ...new Set(
          batches.items.flatMap((batch) =>
            batch.scheduled_fire_at !== null && batch.template_id !== null
              ? [batch.template_id]
              : [],
          ),
        ),
      ].filter((templateId) => !loadedTemplateIds.has(templateId)),
    [batches.items, loadedTemplateIds],
  );
  const referencedTemplateQueries = useQueries({
    queries: isShowcaseMode
      ? []
      : missingScheduledTemplateIds.map((templateId) => ({
          queryKey: queryKeys.templates.detail(templateId),
          queryFn: async () => {
            const { data, error } = await apiClient.GET("/v1/templates/{id}", {
              params: { path: { id: templateId } },
            });
            if (error) {
              throw new ApiRequestError(error);
            }
            return data;
          },
        })),
  });
  const templateNamesById = useMemo(() => {
    const names = new Map(
      templates.items.map((template) => [template.id, template.display_name]),
    );
    for (const query of referencedTemplateQueries) {
      if (query.data !== undefined) {
        names.set(query.data.id, query.data.display_name);
      }
    }
    return names;
  }, [referencedTemplateQueries, templates.items]);

  const { active, history } = useMemo(() => {
    const activeRows: BatchResponse[] = [];
    const historyRows: BatchResponse[] = [];
    for (const batch of batches.items) {
      (isActiveBatch(batch) ? activeRows : historyRows).push(batch);
    }
    return { active: activeRows, history: historyRows };
  }, [batches.items]);

  const openInTranscripts = useCallback(
    (batchId: string) => {
      void navigate({
        search: ({ agent, team }: WorkingScopeSearch) => ({
          agent,
          batch: batchId,
          team,
        }),
        to: "/transcripts",
      });
    },
    [navigate],
  );

  const openCreate = useCallback(
    (fromBatchId?: string) => {
      void navigate({
        search: ({ agent, team }: WorkingScopeSearch) =>
          fromBatchId === undefined
            ? { agent, team }
            : { agent, from: fromBatchId, team },
        to: "/simulations/new",
      });
    },
    [navigate],
  );
  const runTemplate = useCallback(
    (templateId: string, templateAgentId: string) => {
      void navigate({
        search: ({ team }: WorkingScopeSearch) => ({
          agent: templateAgentId,
          team,
          template: templateId,
        }),
        to: "/simulations/new",
      });
    },
    [navigate],
  );

  // Rerun replays the immutable snapshot byte-identical (`frozen`,
  // docs/14 §4.1) — the queue's "run it again" affordance; `latest`/`version`
  // reruns stay on the diff/comparison surfaces. The child batch lands at the
  // top of the queue and is highlighted like a fresh launch.
  const rerunBatch = useMutation({
    mutationFn: async (batchId: string) => {
      const { data, error } = await apiClient.POST("/v1/batches/{id}:rerun", {
        body: { agent_version_id: null, mode: "frozen" },
        params: {
          header: { "Idempotency-Key": createIdempotencyKey() },
          path: { id: batchId },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onError: (error: unknown) => {
      toast.error(requestErrorDescription(error, "Rerun failed."));
    },
    onSuccess: async (child) => {
      toast.success("Rerun launched.");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.batches.all,
      });
      void navigate({
        replace: true,
        search: mergeSearch<SimulationsSearchState>({ launched: child.id }),
      });
    },
  });

  const queue = (
    <SimulationQueue
      activeBatches={active}
      errorMessage={batches.errorMessage}
      hasNextPage={batches.hasNextPage}
      highlightedBatchId={search.launched}
      historyBatches={history}
      isFetchingNextPage={batches.isFetchingNextPage}
      isPending={batches.isPending}
      isRerunPending={rerunBatch.isPending}
      listStateKey={batches.listStateKey}
      onDuplicate={(batchId) => {
        openCreate(batchId);
      }}
      onLaunchFirst={() => {
        openCreate();
      }}
      onLoadMore={batches.fetchNextPage}
      onOpenInTranscripts={openInTranscripts}
      onRerun={(batchId) => {
        rerunBatch.mutate(batchId);
      }}
      templateNamesById={templateNamesById}
    />
  );

  return (
    <Page variant="workspace">
      {/* The page action is tab-contextual (the alerts precedent): the queue
          launches a simulation, the templates table adds a template. */}
      <PageHeader
        actions={
          isShowcaseMode ? (
            <ShowcaseReadOnlyNotice />
          ) : activeTab === "templates" ? (
            <Button size="sm" onClick={openTemplateCreate}>
              Create template
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => {
                openCreate();
              }}
            >
              Create simulation
            </Button>
          )
        }
        tabs={
          isShowcaseMode ? undefined : (
            <Tabs
              activeId={activeTab}
              ariaLabel="Simulations"
              onSelect={(id) => {
                onTabChange(id as SimulationsTab);
              }}
              tabs={simulationTabs}
            />
          )
        }
      />
      {isShowcaseMode ? (
        queue
      ) : (
        <>
          <TabPanel activeId={activeTab} fill tabId="runs">
            {queue}
          </TabPanel>
          <TabPanel activeId={activeTab} fill tabId="templates">
            <TemplatesList
              onOpenEdit={openTemplateEdit}
              onOpenInTranscripts={openInTranscripts}
              onRun={runTemplate}
              scopedAgentId={agentId}
            />
          </TabPanel>
        </>
      )}
    </Page>
  );
}
